/* eslint-disable @typescript-eslint/no-unused-expressions */

import { round } from '@/utils/mathUtils';
import dayjs from 'dayjs';
import JsPDF from 'jspdf';
import type { RowInput, UserOptions } from 'jspdf-autotable';
import { applyPlugin } from 'jspdf-autotable';
import { PDFDocument } from 'pdf-lib';

import type { IInvoiceDetails } from '@/pages/PurchasesContainer/IncomingInvoice/api/IncomingInvoice.interface';
import { AmiriRegular } from '@/utils/fonts';

interface SupplierPrintData {
  supplierName: string;
  commercialRegistration: string;
  taxCard: string;
  invoiceNumber: string;
  invoiceDate: string;
  clientCode: string;
}

import { Arial } from '../fonts/Arial';
import { NotoNaskh } from '../fonts/NotoNaskh';
import { TimeNewRoman } from '../fonts/TimeNewRoman';
import { TimeNewRomanBold } from '../fonts/TimeNewRomanBold';

applyPlugin(JsPDF);

interface JsPDFCustom extends JsPDF {
  autoTable: (options: UserOptions) => void;
}

function fixEngArabMix(data: string) {
  // First, fix parentheses direction for Arabic context
  const processedData = data
    .replace(/\)([^()]*[\u0600-\u06FF][^()]*)\(/g, '($1)') // Fix reversed parentheses around Arabic text
    .replace(/\)([^()]*)\(/g, '($1)'); // Fix any other reversed parentheses

  const words = processedData.split(' ');
  const englishWords = [];
  const arabicWords = [];
  let currentLanguage = /[a-zA-Z]/.test(words[0]) ? 'english' : 'arabic';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const isEnglish = /[a-zA-Z]/.test(word);
    const hasArabic = /[\u0600-\u06FF]/.test(word);

    // Handle mixed content (numbers, symbols, parentheses with Arabic)
    if (
      hasArabic ||
      (!isEnglish && !hasArabic && currentLanguage === 'arabic')
    ) {
      arabicWords.push(word);
      currentLanguage = 'arabic';
    } else if (isEnglish) {
      englishWords.push(word);
      currentLanguage = 'english';
    } else {
      // Numbers, symbols, and other characters follow current context
      currentLanguage === 'english'
        ? englishWords.push(word)
        : arabicWords.push(word);
    }
  }

  // Return with proper spacing
  const englishPart = englishWords.join(' ').trim();
  const arabicPart = arabicWords.join(' ').trim();

  if (englishPart && arabicPart) {
    return `${englishPart} ${arabicPart}`;
  }
  return englishPart || arabicPart;
}

function numberToArabicText(num: number): string {
  if (num === 0) return 'صفر جنيه مصري';

  const ones = [
    '',
    'واحد',
    'اثنان',
    'ثلاثة',
    'أربعة',
    'خمسة',
    'ستة',
    'سبعة',
    'ثمانية',
    'تسعة',
    'عشرة',
    'أحد عشر',
    'اثنا عشر',
    'ثلاثة عشر',
    'أربعة عشر',
    'خمسة عشر',
    'ستة عشر',
    'سبعة عشر',
    'ثمانية عشر',
    'تسعة عشر',
  ];

  const tens = [
    '',
    '',
    'عشرون',
    'ثلاثون',
    'أربعون',
    'خمسون',
    'ستون',
    'سبعون',
    'ثمانون',
    'تسعون',
  ];

  const hundreds = [
    '',
    'مائة',
    'مائتان',
    'ثلاثمائة',
    'أربعمائة',
    'خمسمائة',
    'ستمائة',
    'سبعمائة',
    'ثمانمائة',
    'تسعمائة',
  ];

  function convertHundreds(n: number): string {
    let result = '';

    if (n >= 100) {
      result += hundreds[Math.floor(n / 100)];
      n %= 100;
      if (n > 0) result += ' و ';
    }

    if (n >= 20) {
      result += tens[Math.floor(n / 10)];
      n %= 10;
      if (n > 0) result += ' و ';
    }

    if (n > 0) {
      result += ones[n];
    }

    return result;
  }

  function convertThousands(n: number): string {
    if (n === 0) return '';

    let result = '';

    if (n >= 1000000) {
      const millions = Math.floor(n / 1000000);
      if (millions === 1) {
        result += 'مليون';
      } else if (millions === 2) {
        result += 'مليونان';
      } else if (millions <= 10) {
        result += convertHundreds(millions) + ' ملايين';
      } else {
        result += convertHundreds(millions) + ' مليون';
      }
      n %= 1000000;
      if (n > 0) result += ' و ';
    }

    if (n >= 1000) {
      const thousands = Math.floor(n / 1000);
      if (thousands === 1) {
        result += 'ألف';
      } else if (thousands === 2) {
        result += 'ألفان';
      } else if (thousands <= 10) {
        result += convertHundreds(thousands) + ' آلاف';
      } else {
        result += convertHundreds(thousands) + ' ألف';
      }
      n %= 1000;
      if (n > 0) result += ' و ';
    }

    if (n > 0) {
      result += convertHundreds(n);
    }

    return result;
  }

  // Split the number into pounds and piasters
  const pounds = Math.floor(num);
  const piasters = Math.round((num - pounds) * 100);

  let result = '';

  if (pounds > 0) {
    result += convertThousands(pounds);
    if (pounds === 1) {
      result += ' جنيه مصري';
    } else if (pounds === 2) {
      result += ' جنيهان مصريان';
    } else if (pounds <= 10) {
      result += ' جنيهات مصرية';
    } else {
      result += ' جنيه مصري';
    }
  }

  if (piasters > 0) {
    if (pounds > 0) result += ' و ';
    result += convertHundreds(piasters);
    if (piasters === 1) {
      result += ' قرش';
    } else if (piasters === 2) {
      result += ' قرشان';
    } else if (piasters <= 10) {
      result += ' قروش';
    } else {
      result += ' قرش';
    }
  }

  return result || 'صفر جنيه مصري';
}

export async function mergePdfsIncomeInvoices(pdfsToMerges: ArrayBuffer[]) {
  const mergedPdf = await PDFDocument.create();

  const actions = pdfsToMerges.map(async (pdfBuffer) => {
    const pdf = await PDFDocument.load(pdfBuffer);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => {
      mergedPdf.addPage(page);
    });
  });
  await Promise.all(actions);
  const mergedPdfFile = await mergedPdf.save();
  const pdfDataUri = URL.createObjectURL(
    new Blob([mergedPdfFile.buffer as ArrayBuffer], {
      type: 'application/pdf',
    }),
  );
  // Open a new window with the merged PDF file
  window.open(pdfDataUri);
}

export const PrintIncomeInvoice = async (
  items: IInvoiceDetails,
  collection: boolean,
  supplierData: SupplierPrintData,
) => {
  const doc = new JsPDF('p', 'pt', 'a4', true) as JsPDFCustom;

  const invoiceBody = items?.transactions
    .sort((a, b) => (a.Name > b.Name ? 1 : -1))
    ?.map((data, i) => {
      // Combine batch number and expiry date in one column
      const batchAndExpiry = `${data?.BNO || ''}\n${
        data?.ExpireDate
          ? dayjs(new Date(data?.ExpireDate)).format('YY/MM')
          : ''
      }`;

      // Handle Tax and Bonus with "-" for 0/null/undefined
      const taxValue = data?.Tax && data.Tax !== 0 ? round(data.Tax, 2) : '-';
      const bonusValue =
        data?.Bonus && data.Bonus !== 0 ? round(data.Bonus, 2) : '-';

      return [
        batchAndExpiry, // Batch and expiry date
        round(data.Qty * data.UnitPrice * (1 - data.Discount * 0.01), 2), // Total
        bonusValue, // Bonus
        taxValue, // Tax
        round(data.Discount, 2), // Discount without %
        round(data.Qty), // Quantity
        round(data.UnitPrice, 2), // Unit Price
        fixEngArabMix(`${data.Name}`), // Item name
        ` ${i + 1}`, // Index
      ];
    }) as unknown as RowInput[];
  // Convert string to Uint8ClampedArray

  // const total = items?.transactions?.reduce((acc, item) => {
  //   const price = item.UnitPrice * item.Qty * (1 - item.Discount * 0.01);
  //   return acc + price;
  // }, 0);

  doc.addFileToVFS('NotoNaskh-Regular.ttf', NotoNaskh);
  doc.addFont('NotoNaskh-Regular.ttf', 'NotoNaskh', 'normal');
  doc.addFileToVFS('AmiriRegular.ttf', AmiriRegular);
  doc.addFont('AmiriRegular.ttf', 'AmiriRegular', 'normal');
  doc.addFileToVFS('TimeNewRoman.ttf', TimeNewRoman);
  doc.addFont('TimeNewRoman.ttf', 'TimeNewRoman', 'normal');
  doc.addFileToVFS('TimeNewRomanBold.ttf', TimeNewRomanBold);
  doc.addFont('TimeNewRomanBold.ttf', 'TimeNewRomanBold', 'normal');
  doc.addFileToVFS('Arial.ttf', Arial);
  doc.addFont('Arial.ttf', 'Arial', 'normal');

  doc.setFont('Arial');
  doc.setFillColor(0, 0, 0);
  doc.setFillColor(255, 253, 247);
  doc.setTextColor(0, 0, 0);
  doc.setDrawColor(174, 174, 174);

  // Top line: Client name and فاتورة بيع - justify between (inversed)
  doc.setFontSize(20);
  doc.setTextColor(0, 0, 0);
  doc.text(supplierData.supplierName, 50, 40, { align: 'left' });
  doc.text('فاتورة بيع', 550, 40, { align: 'right' });

  // Registration info directly under header - justify between
  doc.setFontSize(12);
  doc.setTextColor(60, 60, 60);
  doc.text(`بطاقة ضريبية : ${supplierData.taxCard}`, 50, 60, {
    align: 'left',
  });
  doc.text(`سجل تجاري : ${supplierData.commercialRegistration}`, 550, 60, {
    align: 'right',
  });

  // Draw a line after registration info
  doc.setLineWidth(1);
  doc.line(50, 75, 550, 75);

  // العميل and company info with client code - RTL layout
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 40);
  doc.text(
    `العميل : شركة هيلثي للصناعات الدوائية و الطبية - المنصورة - كود العميل : ${supplierData.clientCode}`,
    550,
    90,
    {
      align: 'right',
    },
  );

  // Client address (right-aligned, under client name)
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(
    'عنوان العميل : 17 شارع شريف الراضي توريل القديمة المنصورة الدقهلية برج هلثي',
    550,
    105,
    {
      align: 'right',
    },
  );

  // Document info - justify between (right and left)
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  doc.text(`تاريخ الفاتورة : ${supplierData.invoiceDate}`, 550, 125, {
    align: 'right',
  });
  doc.text(`رقم الفاتورة : ${supplierData.invoiceNumber}`, 50, 125, {
    align: 'left',
  });

  // Total invoice amount in Arabic text (right-aligned)
  const totalAmount = items?.header?.TotalInvoice || 0;
  const totalInvoiceText = numberToArabicText(totalAmount);
  doc.setFontSize(11);
  doc.setTextColor(40, 40, 40);
  doc.text(`إجمالي الفاتورة : ${totalAmount.toFixed(2)} جنيه مصري`, 550, 145, {
    align: 'right',
  });

  // Total amount in Arabic text with "فقط لا غير" (right-aligned)
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(`${totalInvoiceText} فقط لا غير`, 550, 160, {
    align: 'right',
  });

  // Draw separator line
  doc.setLineWidth(0.5);
  doc.line(50, 175, 550, 175);
  doc.setFontSize(12);
  doc.autoTable({
    theme: 'plain',
    startY: 185,
    headStyles: {
      lineColor: [91, 91, 91],
      font: 'TimeNewRomanBold',
      halign: 'center',
      minCellWidth: 30,
      fontStyle: 'bold',
      fontSize: 9,
      lineWidth: 0.1,
      valign: 'top',
      cellPadding: 1,
      // fillColor: "#f5f5f5",
      textColor: [0, 0, 0],
    },
    bodyStyles: {
      lineColor: [91, 91, 91],
      font: 'TimeNewRoman',
      textColor: [0, 0, 0],
      halign: 'center',
      overflow: 'linebreak',
      fontStyle: 'bold',
      cellWidth: 'auto',
      fontSize: 10,
      minCellWidth: 50,
      lineWidth: 0.1,
      cellPadding: 2,
    },
    columnStyles: {
      0: {
        // Batch and expiry date column
        fontSize: 9,
        halign: 'center',
        valign: 'middle',
        minCellWidth: 70,
        cellWidth: 'wrap',
      },
      1: {
        // Total column
        fontStyle: 'bold',
        fontSize: 10,
        halign: 'center',
        valign: 'middle',
        minCellWidth: 60,
      },
      2: {
        // Bonus column
        fontSize: 10,
        halign: 'center',
        valign: 'middle',
        minCellWidth: 40,
      },
      3: {
        // Tax column
        fontSize: 10,
        halign: 'center',
        valign: 'middle',
        minCellWidth: 40,
      },
      4: {
        // Discount column
        fontSize: 10,
        halign: 'center',
        valign: 'middle',
        font: 'TimeNewRomanBold',
        minCellWidth: 35,
      },
      5: {
        // Quantity column
        fontStyle: 'bold',
        fontSize: 10,
        halign: 'center',
        valign: 'top',
        minCellWidth: 35,
      },
      6: {
        // Unit Price column
        fontSize: 10,
        halign: 'center',
        valign: 'middle',
        fontStyle: 'bold',
        minCellWidth: 40,
      },
      7: {
        // Item name column - widest with text wrapping
        fontSize: 11,
        halign: 'right',
        valign: 'top',
        minCellWidth: 150,
        cellWidth: 'wrap',
        overflow: 'linebreak',
      },
      8: {
        // Index column
        fontStyle: 'normal',
        fontSize: 10,
        halign: 'center',
        valign: 'top',
        cellWidth: 'wrap',
        minCellWidth: 25,
      },
    },
    margin: { top: 35, right: 30, bottom: 30, left: 30 },
    head: [
      [
        ' التشغيلة /  الصلاحية',
        'الاجمالي',
        'بونص',
        'ضريبة',
        'الخصم',
        'الكمية',
        'الجمهور',
        'اسم الصنف',
        '',
      ],
    ],

    body: invoiceBody,
  });

  // Add total invoice amount at the bottom after table
  const finalY = (doc as any).lastAutoTable.finalY || 400; // Get the Y position after table

  // Total invoice amount in numbers (right-aligned)
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text(
    `إجمالي الفاتورة : ${totalAmount.toFixed(2)} جنيه مصري`,
    550,
    finalY + 20,
    {
      align: 'right',
    },
  );

  // Total amount in Arabic text with "فقط لا غير" (right-aligned)
  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  doc.text(`${totalInvoiceText} فقط لا غير`, 550, finalY + 35, {
    align: 'right',
  });

  if (!collection) {
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    window.open(pdfUrl);

    // doc.save(` test.pdf`);
  }
  const x = doc.output('arraybuffer');
  return x;
};
